// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import MessageReferences from '@app/features/messaging/state/MessageReferences';
import {SystemMessageUtils} from '@app/features/messaging/utils/SystemMessageUtils';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import Relationships from '@app/features/relationship/state/Relationships';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import Notification, {TTSNotificationMode} from '@app/features/ui/state/Notification';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {TtsUnsupportedModal} from '@app/features/voice/components/alerts/TtsUnsupportedModal';
import * as TtsSpeechUtils from '@app/features/voice/utils/TtsSpeechUtils';
import {formatMessageForTts} from '@app/features/voice/utils/TtsTextFormatter';
import * as TtsWasmFallback from '@app/features/voice/utils/TtsWasmFallback';
import {MessageTypes} from '@app/vendor/constants/ChannelConstants';
import type {Message} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import type {I18n} from '@lingui/core';
import {msg, plural} from '@lingui/core/macro';
import {reaction} from 'mobx';

const MAX_RECENT_MESSAGES = 10;

const MAX_TEXT_LENGTH = 200;

let recentMessageIds: Array<string> = [];

let currentMessage: {channelId: string; messageId: string} | null = null;

let selectedVoice: SpeechSynthesisVoice | null = null;

let speaking = false;

let initialized = false;

let disposers: Array<() => void> = [];

let i18n: I18n | null = null;

let preferWasmFallback = false;
function setI18n(instance: I18n): void {
	i18n = instance;
}
function showUnsupportedModal(): void {
	ContextMenuCommands.close();
	ModalCommands.push(modal(() => <TtsUnsupportedModal />));
}
function pickPreferredVoice(candidates: Array<SpeechSynthesisVoice>): SpeechSynthesisVoice | null {
	if (candidates.length === 0) {
		return null;
	}
	return candidates.find((v) => !v.localService) ?? candidates[0];
}
function findVoiceForLocale(locale: string): SpeechSynthesisVoice | null {
	const voices = TtsSpeechUtils.getVoices();
	if (voices.length === 0) {
		return null;
	}
	const exactMatches = voices.filter((v) => v.lang === locale);
	if (exactMatches.length > 0) {
		return pickPreferredVoice(exactMatches);
	}
	const prefix = locale.split('-')[0];
	const prefixMatches = voices.filter((v) => v.lang?.startsWith(prefix));
	if (prefixMatches.length > 0) {
		return pickPreferredVoice(prefixMatches);
	}
	const defaultVoice = voices.find((v) => v.default);
	return defaultVoice ?? voices[0] ?? null;
}
function refreshVoices(): void {
	const locale = UserSettings.locale;
	selectedVoice = findVoiceForLocale(locale);
}
function stopSpeaking(): void {
	TtsSpeechUtils.cancel();
	TtsWasmFallback.cancel();
	currentMessage = null;
	speaking = false;
}
function nativeHasUsableVoices(): boolean {
	return TtsSpeechUtils.isSupported() && TtsSpeechUtils.getVoices().length > 0;
}
function addRecentMessageId(messageId: string): void {
	recentMessageIds = [messageId, ...recentMessageIds.filter((id) => id !== messageId)].slice(0, MAX_RECENT_MESSAGES);
}
function hasRecentlySpoken(messageId: string): boolean {
	return recentMessageIds.includes(messageId);
}
interface SpeakTextOptions {
	text: string;
	interrupt?: boolean;
	maxLength?: number;
	rate?: number;
	onStart?: () => void;
	onEnd?: () => void;
	onError?: (error: string) => void;
}
function speakText(options: SpeakTextOptions): void {
	const {text, interrupt = true, maxLength = MAX_TEXT_LENGTH, rate, onStart, onEnd, onError} = options;
	if (interrupt) {
		stopSpeaking();
	}
	if (preferWasmFallback || !TtsSpeechUtils.isSupported() || !nativeHasUsableVoices()) {
		speakViaWasm({text, maxLength, rate, onStart, onEnd, onError});
		return;
	}
	if (selectedVoice === null) {
		refreshVoices();
	}
	const utterance = TtsSpeechUtils.createUtterance(text, maxLength);
	if (!utterance) {
		return;
	}
	if (rate !== undefined) {
		utterance.rate = rate;
	}
	utterance.onstart = () => {
		speaking = true;
		onStart?.();
	};
	utterance.onend = () => {
		speaking = false;
		currentMessage = null;
		onEnd?.();
	};
	utterance.onerror = (event) => {
		if (event.error === 'canceled' || event.error === 'interrupted') {
			return;
		}
		speaking = false;
		currentMessage = null;
		preferWasmFallback = true;
		speakViaWasm({text, maxLength, rate, onStart, onEnd, onError, suppressOnStart: false});
	};
	TtsSpeechUtils.speak(utterance, selectedVoice);
}
function speakViaWasm(options: SpeakTextOptions & {suppressOnStart?: boolean}): void {
	const {text, maxLength = MAX_TEXT_LENGTH, rate, onStart, onEnd, onError, suppressOnStart} = options;
	const truncated = text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;
	void TtsWasmFallback.speak({
		text: truncated,
		rate,
		onStart: () => {
			speaking = true;
			if (!suppressOnStart) onStart?.();
		},
		onEnd: () => {
			speaking = false;
			currentMessage = null;
			onEnd?.();
		},
		onError: (err) => {
			speaking = false;
			currentMessage = null;
			onError?.(err);
		},
	});
}
function speakMessage(content: string): void {
	if (!TtsSpeechUtils.isSupported() && TtsWasmFallback.hasFailed()) {
		showUnsupportedModal();
		return;
	}
	if (speaking) {
		stopSpeaking();
		return;
	}
	speakText({text: content});
}
function isUserMessageType(type: number): boolean {
	return type === MessageTypes.DEFAULT || type === MessageTypes.REPLY || type === MessageTypes.CLIENT_SYSTEM;
}
function describeNonTextContent(message: Message, localI18n: I18n): string | null {
	if (message.stickers && message.stickers.length > 0) {
		return localI18n._(msg`sent a sticker`);
	}
	if (message.attachments && message.attachments.length > 0) {
		if (message.attachments.length === 1) {
			return localI18n._(msg`sent an attachment`);
		}
		return localI18n._(
			plural(
				{count: message.attachments.length},
				{
					one: 'sent # attachment',
					other: 'sent # attachments',
				},
			),
		);
	}
	if (message.embeds && message.embeds.length > 0) {
		return localI18n._(msg`sent an embed`);
	}
	return null;
}
function shouldSpeakMessage(message: Message): boolean {
	const mode = Notification.ttsNotificationMode;
	const isExplicitTts = message.tts === true;
	const isSelf = message.author.id === Authentication.currentUserId;
	const isSystemMessage = !isUserMessageType(message.type);
	if (isSelf && !isSystemMessage) {
		return false;
	}
	if (mode === TTSNotificationMode.NEVER) {
		return isExplicitTts && Accessibility.enableTTSCommand;
	}
	if (mode === TTSNotificationMode.FOR_CURRENT_CHANNEL) {
		if (message.channel_id !== SelectedChannel.currentChannelId) {
			return false;
		}
	}
	return true;
}
function handleIncomingTtsMessage(message: Message): void {
	if (!TtsSpeechUtils.isSupported() && TtsWasmFallback.hasFailed()) {
		return;
	}
	if (!shouldSpeakMessage(message)) {
		return;
	}
	if (hasRecentlySpoken(message.id)) {
		return;
	}
	if (Relationships.isBlocked(message.author.id)) {
		return;
	}
	const channel = Channels.getChannel(message.channel_id);
	if (!channel) {
		return;
	}
	if (
		UserGuildSettings.isGuildOrChannelMuted(channel.guildId ?? null, channel.id) ||
		UserGuildSettings.isCategoryMuted(channel.guildId ?? null, channel.id)
	) {
		return;
	}
	if (!i18n) {
		return;
	}
	if (!isUserMessageType(message.type)) {
		const systemText = SystemMessageUtils.stringify(message, i18n);
		if (!systemText) {
			return;
		}
		addRecentMessageId(message.id);
		currentMessage = {channelId: message.channel_id, messageId: message.id};
		speakText({text: systemText});
		return;
	}
	const author = Users.getUser(message.author.id);
	if (!author) {
		return;
	}
	const authorName = NicknameUtils.getNickname(author, channel.guildId ?? undefined);
	if (!message.content.trim()) {
		const description = describeNonTextContent(message, i18n);
		if (!description) {
			return;
		}
		addRecentMessageId(message.id);
		currentMessage = {channelId: message.channel_id, messageId: message.id};
		speakText({text: i18n._(msg`${authorName} ${description}`)});
		return;
	}
	let replyAuthorName: string | null = null;
	if (message.message_reference?.message_id) {
		const refChannelId = message.message_reference.channel_id ?? message.channel_id;
		const refMessageId = message.message_reference.message_id;
		const ref = MessageReferences.getMessageReference(refChannelId, refMessageId);
		if (ref.message) {
			const replyAuthor = Users.getUser(ref.message.author.id);
			if (replyAuthor) {
				replyAuthorName = NicknameUtils.getNickname(replyAuthor, channel.guildId ?? undefined);
			}
		}
	}
	const formattedText = formatMessageForTts(
		message.content,
		authorName,
		channel.guildId ?? null,
		i18n,
		replyAuthorName,
	);
	addRecentMessageId(message.id);
	currentMessage = {channelId: message.channel_id, messageId: message.id};
	speakText({text: formattedText});
}
function handleMessageDelete(channelId: string, messageId: string): void {
	if (currentMessage?.channelId === channelId && currentMessage?.messageId === messageId) {
		stopSpeaking();
	}
}
function handleChannelSelect(channelId: string | null): void {
	if (currentMessage && currentMessage.channelId !== channelId) {
		stopSpeaking();
	}
}
function init(): void {
	if (initialized) {
		return;
	}
	initialized = true;
	window.setTimeout(() => {
		if (!nativeHasUsableVoices()) {
			void TtsWasmFallback.preload();
		}
	}, 1500);
	if (!TtsSpeechUtils.isSupported()) {
		return;
	}
	window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
	refreshVoices();
	disposers.push(
		reaction(
			() => SelectedChannel.currentChannelId,
			(channelId) => handleChannelSelect(channelId),
		),
	);
	disposers.push(
		reaction(
			() => UserSettings.locale,
			() => refreshVoices(),
		),
	);
}
function dispose(): void {
	if (!initialized) {
		return;
	}
	stopSpeaking();
	if (TtsSpeechUtils.isSupported()) {
		window.speechSynthesis.removeEventListener('voiceschanged', refreshVoices);
	}
	for (const disposer of disposers) {
		disposer();
	}
	disposers = [];
	recentMessageIds = [];
	selectedVoice = null;
	initialized = false;
}
function isSupported(): boolean {
	return TtsSpeechUtils.isSupported() || !TtsWasmFallback.hasFailed();
}
function isSpeaking(): boolean {
	return speaking;
}
function hasVoices(): boolean {
	return selectedVoice !== null || TtsSpeechUtils.getVoices().length > 0;
}
function isUsingFallback(): boolean {
	return preferWasmFallback || !nativeHasUsableVoices();
}
interface SpeakOptions {
	rate?: number;
	onEnd?: () => void;
	onError?: (error: string) => void;
}
function speak(text: string, options?: SpeakOptions): void {
	speakText({
		text,
		maxLength: 2000,
		rate: options?.rate,
		onEnd: options?.onEnd,
		onError: options?.onError,
	});
}
function stop(): void {
	stopSpeaking();
}

export default {
	init,
	dispose,
	setI18n,
	isSupported,
	isSpeaking,
	hasVoices,
	isUsingFallback,
	speak,
	stop,
	speakMessage,
	stopSpeaking,
	handleIncomingTtsMessage,
	handleMessageDelete,
	handleChannelSelect,
};
